import * as reselect from "reselect";
import { createSelector } from "reselect";
import {
    getCall,
    getCallClientState, getCallId,
    getCallState,
    getCallStatus,
    getDeviceManager,
    getEnvironmentInfo,
    getIsScreenShareOn,
    getLocalVideoStreams,
} from "./baseSelectors";
import { isSafari } from "../../utils";
import { getUserFacingDiagnostics } from "../selectors/baseSelectors";
import { LocalVideoStreamState } from "@azure/communication-react";
import { isInCall, isPreviewOn } from "../../utils/callUtils";
import { CallState as SDKCallStatus } from "@azure/communication-calling";


export const devicePermissionSelector = reselect.createSelector(
    getDeviceManager,
    (deviceManager) => ({
        video: deviceManager.deviceAccess ? deviceManager.deviceAccess.video : undefined,
        audio: deviceManager.deviceAccess ? deviceManager.deviceAccess.audio : undefined
    })
);


export const localPreviewSelector = reselect.createSelector([getDeviceManager], (deviceManager) => {
    // TODO: we should take in a LocalVideoStream that developer wants to use as their 'Preview' view. We should also
    // handle cases where 'Preview' view is in progress and not necessary completed.
    const view =
        deviceManager.cameras.length > 0 && // This ensures that the video stream is reset if camera devices are removed.
        deviceManager.unparentedViews.length > 0 &&
        deviceManager.unparentedViews[0].view;
    return {
        videoStreamElement: view ? view.target : null
    };
});

export const selectIsSafari = reselect.createSelector(
    [getEnvironmentInfo],
    (environmentInfo) => isSafari(environmentInfo)
);

export const selectCall = reselect.createSelector(
    [getCall],
    call => call
);

export const mutedNotificationSelector = reselect.createSelector(
    [getUserFacingDiagnostics],
    (diagnostics) => ({
        speakingWhileMuted: !!diagnostics?.media.latest.speakingWhileMicrophoneIsMuted?.value
    })
);

export const callStatusSelector = reselect.createSelector(
  [getCallStatus, getIsScreenShareOn],
  (callStatus: SDKCallStatus, isScreenShareOn) => ({
      callStatus,
      isScreenShareOn
    })
);

export const localVideoSelector = reselect.createSelector(
    [callStatusSelector, getDeviceManager, getLocalVideoStreams],
    (callStatus, deviceManager, localVideoStreams) => {
        let localVideoStream: LocalVideoStreamState | undefined;
        if (isInCall(callStatus.callStatus)) {
            localVideoStream = localVideoStreams?.find((i) => i.mediaStreamType === 'Video');
        } else if (isPreviewOn(deviceManager)) {
            // TODO: we should take in a LocalVideoStream that developer wants to use as their 'Preview' view. We should also
            // handle cases where 'Preview' view is in progress and not necessary completed.
            localVideoStream = deviceManager.unparentedViews[0];
        }

        return {
            isAvailable: !!localVideoStream,
            isMirrored: localVideoStream?.view?.isMirrored,
            renderElement: localVideoStream?.view?.target,
            activeVideoEffects: localVideoStream?.videoEffects
        };
    }
);


export const selectRaisedHandsState = createSelector(
    [getCallState],
    (state) => ({
        localParticipantRaisedHand: state?.raiseHand?.localParticipantRaisedHand,
        raisedHands: state?.raiseHand?.raisedHands,

    })
);

export const selectIsHandRaised = createSelector(
    [selectRaisedHandsState],
    (state) => state.localParticipantRaisedHand !== undefined
);

export const selectNumberOfRaisedHands = createSelector(
    [selectRaisedHandsState],
    (state) => state.raisedHands?.length
);

export const selectCallId = createSelector(
    [getCallId],
    (callId) => callId
);

export const selectCallClientState = createSelector(
    [getCallClientState],
    (callClientState) => callClientState
);

export const selectCallState = createSelector(
    [getCallId, getCallClientState],
    (callId, clientState) => callId ? clientState?.calls[callId] : undefined
);


export const selectIsMuted = createSelector(
    [selectCallState],
    state => state?.isMuted
);

// export const selectIsMuted = createSelector(
//     [getCallState],
//     (callState) => callState?.isMuted
// );
